import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, TextField, Button, Typography,
  Chip, Box, CircularProgress, InputAdornment, IconButton, Paper,
  Alert, Snackbar, AppBar, Toolbar, Badge, Pagination, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,Drawer,
  List, ListItem, ListItemText, ListItemAvatar, Avatar
} from '@mui/material';
import {
  Search, Add, Remove, ArrowForward, LocalPharmacy, ShoppingCart,
  ArrowBack, CloudUpload, Close, CheckCircle
} from '@mui/icons-material';
import { apiRequest } from '../../fetchSetup';
import { Divider } from '@mui/material';
import PatientDetailsForm from "./PatientDetailsForm";
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import  bg2 from "../Logos/med_background2.png"



const MedicineListScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userEmail, orderType } = location.state || {};

  const [medicines, setMedicines] = useState([]);
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cart, setCart] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Patient form
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);

  // Prescription
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [prescriptionPreview, setPrescriptionPreview] = useState(null);

  const categories = ['All', 'Tablet', 'Syrup', 'Injection', 'Capsules', 'Cream', 'Drop'];
  const [offeredDiscountPct, setOfferedDiscountPct] = useState(null);
  const [showCart, setShowCart] = useState(false);
  // ─── AYUJA pricing state ───────────────────────────────────────────────────
  const [ayujaCartPrices, setAyujaCartPrices] = useState({});
  const [ayujaSummary, setAyujaSummary] = useState(null);
  const lastFetchedCartKey = useRef('');
  const pricingPollRef = useRef(null);

  // ─── AYUJA fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (cart.length === 0) {
      setAyujaCartPrices({});
      setAyujaSummary(null);
      lastFetchedCartKey.current = '';
      sessionStorage.removeItem('ayujaPricing');
      return;
    }

    const cartKey = cart
      .map(i => `${i.medicine_id}:${i.quantity}`)
      .sort()
      .join('|');

    if (cartKey === lastFetchedCartKey.current) return;

    const fetchAyujaPrices = async () => {
      try {
        const response = await apiRequest(
          `${process.env.REACT_APP_MEDICINE_API_URL}/calculate-order-price`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: cart.map(i => ({ medicine_id: i.medicine_id, quantity: i.quantity })),
            }),
          }
        );
        if (!response.ok) { console.error('AYUJA fetch failed:', response.status); return; }
        const data = await response.json();
        const priceMap = {};
        if (data.item_breakdown) {
          data.item_breakdown.forEach(item => { priceMap[item.medicine_id] = item.selling_price; });
        }
        setAyujaCartPrices(priceMap);
        setAyujaSummary(data);
        sessionStorage.setItem('ayujaPricing', JSON.stringify({
          item_breakdown: data.item_breakdown,
          final_discount_pct: data.final_discount_pct,
          alpha: data.alpha,
          ayuja_applied: data.ayuja_applied,
          ayuja_subtotal: data.ayuja_subtotal,
          mrp_total: data.mrp_total,
        }));
        lastFetchedCartKey.current = cartKey;
      } catch (err) {
        console.error('AYUJA fetch error:', err);
      }
    };

    fetchAyujaPrices();
  }, [cart]);

  useEffect(() => {
    const POLL_EVERY = 10 * 1000;
    const CACHE_KEY = 'ayujaPricingConfig';

    const fetchPricingConfig = async (bustCache = false) => {
      if (!bustCache) {
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed._cachedAt || 0);
            if (age < POLL_EVERY && typeof parsed.configured_offered_discount_pct === 'number') {
              setOfferedDiscountPct(prev =>
                prev !== parsed.configured_offered_discount_pct ? parsed.configured_offered_discount_pct : prev
              );
              return;
            }
          }
        } catch { }
      }
      try {
        const response = await apiRequest(
          `${process.env.REACT_APP_MEDICINE_API_URL}/pricing-config`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        if (!response.ok) { setOfferedDiscountPct(prev => prev ?? 20); return; }
        const data = await response.json();
        const newPct = data.configured_offered_discount_pct ?? 20;
        setOfferedDiscountPct(prev => prev !== newPct ? newPct : prev);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, _cachedAt: Date.now() }));
      } catch (err) {
        setOfferedDiscountPct(prev => prev ?? 20);
      }
    };

    fetchPricingConfig(false);
    pricingPollRef.current = setInterval(() => fetchPricingConfig(true), POLL_EVERY);
    const handleVisibility = () => { if (document.visibilityState === 'visible') fetchPricingConfig(true); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(pricingPollRef.current); document.removeEventListener('visibilitychange', handleVisibility); };
  }, []);

  // ─── User fetch ────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUser = async () => {
      setUserLoading(true);
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) { setUser(JSON.parse(storedUser)); }
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      if (userId && token) {
        try {
          const userData = await fetchUserFromRedis(userId, token);
          if (userData) { setUser(userData); sessionStorage.setItem("user", JSON.stringify(userData)); }
        } catch (err) { console.error("Error fetching user from Redis:", err); }
      }
      setUserLoading(false);
    };
    fetchUser();
  }, []);

  // ─── Session restore ────────────────────────────────────────────────────────
  useEffect(() => {
    const savedPatientDetails = sessionStorage.getItem('pharmacyPatientDetails');
    if (savedPatientDetails) {
      try { const parsed = JSON.parse(savedPatientDetails); setPatientDetails(parsed); setShowPatientForm(false); }
      catch (error) { console.error('Error parsing patient details:', error); }
    }
    const savedPrescription = sessionStorage.getItem('pharmacyPrescription');
    const savedPrescriptionPreview = sessionStorage.getItem('pharmacyPrescriptionPreview');
    if (savedPrescription && savedPrescriptionPreview) { setPrescriptionFile(savedPrescription); setPrescriptionPreview(savedPrescriptionPreview); }
    const savedCart = sessionStorage.getItem('pharmacyCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart) && parsedCart.length > 0) setCart(parsedCart);
      } catch (error) { console.error('Error parsing saved cart:', error); }
    }
    fetchMedicines();
  }, []);

  useEffect(() => { filterMedicines(); setCurrentPage(1); }, [searchQuery, selectedCategory, medicines]);

  const fetchMedicines = async () => {
    try {
      const response = await apiRequest(process.env.REACT_APP_GET_MEDICINES);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.medicines && Array.isArray(data.medicines)) {
        const activeMedicines = data.medicines.filter(med => med.is_active !== false);
        setMedicines(activeMedicines);
        setFilteredMedicines(activeMedicines);
      } else {
        showSnackbar('Invalid data format received', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to load medicines: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterMedicines = () => {
    let filtered = medicines;
    if (selectedCategory !== 'All') filtered = filtered.filter(med => med.drug_type === selectedCategory);
    if (searchQuery) {
      filtered = filtered.filter(med =>
        med.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        med.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        med.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredMedicines(filtered);
  };

  const totalPages = Math.ceil(filteredMedicines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentMedicines = filteredMedicines.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (event, value) => { setCurrentPage(value); window.scrollTo({ top: 400, behavior: 'smooth' }); };

  const addToCart = (medicine) => {
    const existingItem = cart.find(item => item.medicine_id === medicine.medicine_id);
    let updatedCart;
    if (existingItem) {
      updatedCart = cart.map(item =>
        item.medicine_id === medicine.medicine_id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      updatedCart = [...cart, { ...medicine, quantity: 1 }];
    }
    setCart(updatedCart);
    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
    if (medicine.stock > 0) showSnackbar('Added to cart', 'success');
  };

  const removeFromCart = (medicineId) => {
    const existingItem = cart.find(item => item.medicine_id === medicineId);
    let updatedCart;
    if (existingItem.quantity === 1) {
      updatedCart = cart.filter(item => item.medicine_id !== medicineId);
      showSnackbar('Removed from cart', 'info');
    } else {
      updatedCart = cart.map(item =>
        item.medicine_id === medicineId ? { ...item, quantity: item.quantity - 1 } : item
      );
    }
    setCart(updatedCart);
    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
  };

  const getCartQuantity = (medicineId) => {
    const item = cart.find(item => item.medicine_id === medicineId);
    return item ? item.quantity : 0;
  };

  const getTotalItems = () => cart.reduce((total, item) => total + item.quantity, 0);
  const getMrpTotal = () => cart.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);
  const getSellingTotal = () => cart.reduce((s, i) => {
    const ayujaPrice = ayujaCartPrices[i.medicine_id];
    return s + (ayujaPrice !== undefined ? ayujaPrice : (i.discounted_price || i.price || 0)) * i.quantity;
  }, 0);

  const navigateToCheckout = () => {
    const cartWithAyujaPrices = cart.map(item => ({
      ...item,
      discounted_price: ayujaCartPrices[item.medicine_id] !== undefined
        ? ayujaCartPrices[item.medicine_id]
        : (item.discounted_price || item.price),
    }));
    const sellingTotal = getSellingTotal();
    sessionStorage.setItem('pharmacyCart', JSON.stringify(cartWithAyujaPrices));
    sessionStorage.setItem('pharmacyPrescription', prescriptionFile || '');
    sessionStorage.setItem('pharmacyPrescriptionPreview', prescriptionPreview || '');
    navigate('/medicine-delivery-address', {
      state: { cart: cartWithAyujaPrices, patientDetails, orderType: orderType || 'pharmacy', userEmail, totalAmount: sellingTotal, prescriptionFile },
    });
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) { showSnackbar('Please add medicines to cart', 'warning'); return; }
    if (!patientDetails) { setShowPatientForm(true); return; }
    const requiresPrescription = cart.some(item => item.prescription_required);
    if (requiresPrescription && !prescriptionFile) { setShowPrescriptionDialog(true); return; }
    navigateToCheckout();
  };

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) { showSnackbar('Please upload a valid image (JPG, PNG) or PDF file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showSnackbar('File size must be less than 5MB', 'error'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setPrescriptionFile(base64String);
      setPrescriptionPreview(base64String);
      sessionStorage.setItem('pharmacyPrescription', base64String);
      sessionStorage.setItem('pharmacyPrescriptionPreview', base64String);
      showSnackbar('Prescription uploaded successfully', 'success');
    };
    reader.readAsDataURL(file);
  };
  useEffect(() => {
  if (showCart) {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  return () => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  };
}, [showCart]);

  const handleRemovePrescription = () => {
    setPrescriptionFile(null);
    setPrescriptionPreview(null);
    sessionStorage.removeItem('pharmacyPrescription');
    sessionStorage.removeItem('pharmacyPrescriptionPreview');
    showSnackbar('Prescription removed', 'info');
  };

  const handlePrescriptionDialogClose = () => setShowPrescriptionDialog(false);

  const handlePrescriptionDialogSubmit = () => {
    if (!prescriptionFile) { showSnackbar('Please upload a prescription to continue', 'warning'); return; }
    setShowPrescriptionDialog(false);
    navigateToCheckout();
  };

  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const navigateToCheckoutWithDetails = (details) => {
    const cartWithAyujaPrices = cart.map(item => ({
      ...item,
      discounted_price: ayujaCartPrices[item.medicine_id] !== undefined
        ? ayujaCartPrices[item.medicine_id]
        : (item.discounted_price || item.price),
    }));
    const sellingTotal = getSellingTotal();
    sessionStorage.setItem('pharmacyCart', JSON.stringify(cartWithAyujaPrices));
    sessionStorage.setItem('pharmacyPrescription', prescriptionFile || '');
    sessionStorage.setItem('pharmacyPrescriptionPreview', prescriptionPreview || '');
    navigate('/medicine-delivery-address', {
      state: { cart: cartWithAyujaPrices, patientDetails: details, orderType: orderType || 'pharmacy', userEmail, totalAmount: sellingTotal, prescriptionFile },
    });
  };

  const handlePatientDetailsComplete = (details) => {
    sessionStorage.setItem('pharmacyPatientDetails', JSON.stringify(details));
    setPatientDetails(details);
    setShowPatientForm(false);
    const requiresPrescription = cart.some(item => item.prescription_required);
    if (requiresPrescription && !prescriptionFile) { setShowPrescriptionDialog(true); return; }
    navigateToCheckoutWithDetails(details);
  };

  const handleBackToPatientForm = () => setShowPatientForm(true);

  // ─── Loading screen ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <CircularProgress size={60} sx={{ color: '#006d77' }} />
        <Typography sx={{ mt: 2, fontSize: 16, color: '#64748b' }}>Loading medicines...</Typography>
      </Box>
    );
  }

  // ─── Patient form screen ───────────────────────────────────────────────────
  if (showPatientForm) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <AppBar position="static" sx={{ bgcolor: '#006d77', boxShadow: 2 }}>
          <Toolbar>
           <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/medicine-order-type')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>Patient Details</Typography>
          </Toolbar>
        </AppBar>
        <Paper elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', py: 2 }}>
          <Container maxWidth="lg">
            <Stepper activeStep={1} alternativeLabel>
              <Step><StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: '#006d77' } },'&.Mui-completed': { color: '#006d77' } }}>Select Medicines</StepLabel></Step>
              <Step><StepLabel>Patient Details</StepLabel></Step>
              <Step><StepLabel>Checkout</StepLabel></Step>
            </Stepper>
          </Container>
        </Paper>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <PatientDetailsForm onComplete={handlePatientDetailsComplete} initialData={patientDetails} userEmail={userEmail} orderType={orderType} />
        </Container>
      </Box>
    );
  }

  // ─── Derived totals ────────────────────────────────────────────────────────
  const mrpTotal = getMrpTotal();
  const sellingTotal = getSellingTotal();
  const totalSavings = mrpTotal - sellingTotal;
  const hasAyuja = Object.keys(ayujaCartPrices).length > 0;

  // ─── Main screen ───────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: cart.length > 0 ? 12 : 2 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundImage: `
                    linear-gradient(90deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.61) 100%),
                    url(${bg2})
                  `,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat", boxShadow: 2 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBackToPatientForm} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>Select Medicines</Typography>
          <IconButton color="inherit" onClick={() => setShowCart(true)}>
          <Badge badgeContent={getTotalItems()} color="error">
            <ShoppingCart />
          </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Stepper */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', py: 2 }}>
        <Container maxWidth="lg">
          <Stepper activeStep={0} alternativeLabel>
            <Step completed ={cart.length>0}>
              <StepLabel StepIconProps={{ sx: { '&.Mui-completed': { color: '#006d77' },'&.Mui-active': { color: '#006d77' } } }}>Select Medicines</StepLabel>
            </Step>
            <Step completed={patientDetails}>
              <StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: '#006d77' },'&.Mui-completed': { color: '#006d77' }
             } }}>Patient Details</StepLabel>
            </Step>
            <Step><StepLabel>Checkout</StepLabel></Step>
          </Stepper>
        </Container>
      </Paper>

      {/* Patient Details Summary */}
      {patientDetails && (
        <Container maxWidth="lg" sx={{ mt: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f9fafb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Patient Details</Typography>
              <Button size="small" onClick={handleBackToPatientForm} sx={{ textTransform: 'none', color: '#006d77', fontSize: 13, fontWeight: 600 }}>Edit Details</Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Name</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.name}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Age</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.dob} years</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Gender</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.gender}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Phone</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.phone}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.primary" sx={{ fontSize: 11 }}>Doctor Name</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.doctorname}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f9fafb', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>Prescription</Typography>
                {prescriptionFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} />
                    <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 600 }}>Prescription uploaded</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>No prescription uploaded</Typography>
                )}
              </Box>
              <Button
                size="small"
                variant={prescriptionFile ? "outlined" : "contained"}
                onClick={() => setShowPrescriptionDialog(true)}
                sx={{
                  textTransform: 'none', fontSize: 13, fontWeight: 600,
                  ...(prescriptionFile
                    ? { borderColor: '#006d77', color: '#006d77', '&:hover': { borderColor: '#005560', bgcolor: '#f0fdfa' } }
                    : { bgcolor: '#006d77', color: '#fff', '&:hover': { bgcolor: '#005560' } })
                }}
              >
                {prescriptionFile ? 'View/Edit Prescription' : 'Upload Prescription'}
              </Button>
            </Box>
          </Paper>
        </Container>
      )}

      {/* Search */}
      <Paper elevation={0} sx={{ bgcolor: '#fff', px: 2, py: 2, borderBottom: '1px solid #e2e8f0', mt: 3 }}>
        <Container maxWidth="lg">
          <TextField
            fullWidth
            placeholder="Search medicines by name, description, or manufacturer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ color: '#006D77' }} /></InputAdornment>,
            }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f1f5f9', borderRadius: 2, '& fieldset': { border: 'none' } } }}
          />
        </Container>
      </Paper>

      {/* Category filter */}
      <Paper elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', py: 2, px: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
            {categories.map((drug_type) => (
              <Chip
                key={drug_type}
                label={drug_type}
                onClick={() => setSelectedCategory(drug_type)}
                sx={{
                  background: selectedCategory === drug_type ? 'linear-gradient(90deg, #006D77 0%, #00838F 100%)' : '#f1f5f9',
                  color: selectedCategory === drug_type ? '#fff' : '#64748b',
                  fontWeight: 600, px: 2, cursor: 'pointer',
                  '&:hover': { bgcolor: selectedCategory === drug_type ? 'linear-gradient(90deg, #004F55 0%, #006D77 100%)' : '#e2e8f0' },
                }}
              />
            ))}
          </Box>
        </Container>
      </Paper>

      {/* Info Banner */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>{filteredMedicines.length} medicines</strong> available
            {cart.length > 0 && <strong> • {cart.length} in cart</strong>}
            {filteredMedicines.length > itemsPerPage && <strong> • Page {currentPage} of {totalPages}</strong>}
          </Typography>
        </Alert>
      </Container>

      {/* Medicine Grid */}
      <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
        {filteredMedicines.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <LocalPharmacy sx={{ fontSize: 80, color: '#cbd5e1' }} />
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#475569', mt: 2 }}>No medicines found</Typography>
            <Typography sx={{ fontSize: 14, color: '#94a3b8', mt: 0.5 }}>Try adjusting your search or filters</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {currentMedicines.map((medicine) => {
                const quantity = getCartQuantity(medicine.medicine_id);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={medicine.medicine_id}>
                    <Card
                      elevation={0}
                      sx={{
                        width: "360px", height: "420px", display: "flex", flexDirection: "column",
                        borderRadius: 2.5, border: "1px solid #e2e8f0", transition: "all 0.3s ease",
                        bgcolor: "#fff", overflow: "hidden",
                        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 24px rgba(0,0,0,0.08)", borderColor: "#006d77" }
                      }}
                    >
                      {medicine.image_url ? (
                        <Box sx={{ width: '100%', height: "100%", overflow: 'hidden', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          <Box component="img" src={medicine.image_url} alt={medicine.name}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                          <Box sx={{ display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', bgcolor: '#f1f5f9' }}>
                            <LocalPharmacy sx={{ fontSize: 60, color: '#cbd5e1' }} />
                            <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1 }}>No image available</Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                          <LocalPharmacy sx={{ fontSize: 60, color: '#cbd5e1' }} />
                          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1 }}>No image available</Typography>
                        </Box>
                      )}

                      <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, color: '#1e293b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {medicine.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                          Pack of {medicine.pack_size}
                        </Typography>
                        {medicine.manufacturer && (
                          <Typography variant="caption" sx={{ fontSize: 12, color: '#64748b', mb: 1, display: 'block' }}>
                            by {medicine.manufacturer}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontSize: 13, color: '#64748b', mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {medicine.description || 'No description available'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                            MRP ₹{medicine.price?.toFixed(2)}
                          </Typography>
                          {medicine.base_price > 0 && medicine.price > medicine.base_price && (
                            <Chip
                              label={
                                offeredDiscountPct !== null
                                  ? `Up to ${offeredDiscountPct}% OFF`
                                  : `Up to ${Math.floor(((medicine.price - medicine.base_price) / medicine.price) * 100)}% OFF`
                              }
                              size="small"
                              sx={{ bgcolor: '#fff7ed', color: '#ea580c', fontWeight: 700, height: 20, fontSize: 11, border: '1px solid #fed7aa', pointerEvents: 'none' }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Chip label={medicine.drug_type} size="small" sx={{ bgcolor: '#dbeafe', color: '#006d77', fontSize: 12, fontWeight: 500, pointerEvents: "none" }} />
                        </Box>

                        {medicine.prescription_required && (
                          <Chip label="Prescription Required" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, mb: 2, width: '100%', pointerEvents: "none" }} />
                        )}

                        <Box sx={{ mt: 'auto' }}>
                          {quantity === 0 ? (
                            <Button
                              fullWidth variant="contained" startIcon={<Add />}
                              onClick={() => addToCart(medicine)}
                              sx={{ background: 'linear-gradient(90deg, #006D77 0%, #00838F 100%)', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 2, '&:hover': { bgcolor: '#005560' }, '&:disabled': { bgcolor: '#cbd5e1', color: '#fff' } }}
                            >
                              Add to Cart
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#f1f5f9', borderRadius: 2, p: 1 }}>
                              <IconButton size="small" onClick={() => removeFromCart(medicine.medicine_id)} sx={{ bgcolor: '#fff', boxShadow: 1, '&:hover': { bgcolor: '#e2e8f0' } }}>
                                <Remove sx={{ fontSize: 18, color: '#006d77' }} />
                              </IconButton>
                              <TextField
                                size="small" type="number" value={quantity}
                                 onChange={(e) => {
                                  const value = e.target.value;

                                  // Allow empty value temporarily while typing
                                  if (value === '') {
                                    const updatedCart = cart.map(item =>
                                      item.medicine_id === medicine.medicine_id
                                        ? { ...item, quantity: '' }
                                        : item
                                    );

                                    setCart(updatedCart);
                                    return;
                                  }

                                  const numValue = parseInt(value);

                                  if (isNaN(numValue) || numValue < 1) return;

                                  const updatedCart = cart.map(item =>
                                    item.medicine_id === medicine.medicine_id
                                      ? { ...item, quantity: numValue }
                                      : item
                                  );

                                  setCart(updatedCart);
                                  sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const updatedCart = cart.map(item =>
                                      item.medicine_id === medicine.medicine_id
                                        ? { ...item, quantity: 1 }
                                        : item
                                    );

                                    setCart(updatedCart);
                                    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
                                  }
                                }}
                                inputProps={{ min: 1, style: { textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1e293b', padding: '6px 8px' } }}
                                sx={{
                                  width: 60, bgcolor: '#fff', borderRadius: 1,
                                  '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: '2px solid #006d77' } },
                                  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                              />
                              <IconButton size="small" onClick={() => addToCart(medicine)} sx={{ bgcolor: '#fff', boxShadow: 1, '&:hover': { bgcolor: '#e2e8f0' } }}>
                                <Add sx={{ fontSize: 18, color: '#006d77' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages} page={currentPage} onChange={handlePageChange}
                  color="primary"  
                  sx={{
                    '& .MuiPaginationItem-root': { fontSize: 16, fontWeight: 600 },
                    '& .Mui-selected': { bgcolor: '#006d77 !important', color: '#fff', '&:hover': { bgcolor: '#005560 !important' } },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* ─── Cart Footer ──────────────────────────────────────────────────────── */}
      {cart.length > 0 && (
        <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: '#fff', borderTop: '2px solid #006d77', zIndex: 1000 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'} in cart
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>MRP ₹{mrpTotal.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#006d77' }}>₹{sellingTotal.toFixed(2)}</Typography>
                {hasAyuja && totalSavings > 0 && (
                  <Typography sx={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                    You save ₹{totalSavings.toFixed(2)}
                    {ayujaSummary?.final_discount_pct ? ` (${ayujaSummary.final_discount_pct.toFixed(1)}% OFF)` : ''}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained" endIcon={<ArrowForward />} onClick={proceedToCheckout}
                sx={{ bgcolor: '#006D77', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: 16, px: 4, py: 1.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,109,119,0.3)', '&:hover': { bgcolor: '#005560', boxShadow: '0 6px 16px rgba(0,109,119,0.4)' } }}
              >
                Proceed to Checkout
              </Button>
            </Box>
          </Container>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Drawer
  anchor="right"
  open={showCart}
  onClose={() => setShowCart(false)}
  ModalProps={{
    keepMounted: true,
    disableScrollLock: false
  }}
  PaperProps={{
    sx: {
      width: { xs: '100%', sm: 420 },
      height:"90%",
      mt:6,
      p: 2,
      bgcolor: '#f8fafc'
    }
  }}
>
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      Cart
    </Typography>

    <IconButton onClick={() => setShowCart(false)}>
      <Close />
    </IconButton>
  </Box>

  {cart.length === 0 ? (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 10
      }}
    >
      <ShoppingCart sx={{ fontSize: 80, color: '#cbd5e1' }} />

      <Typography sx={{ mt: 2, color: '#64748b' }}>
        Your cart is empty
      </Typography>
    </Box>
  ) : (
    <>
      <List sx={{ flex: 1 }}>
        {cart.map((item) => (
          <Paper
            key={item.medicine_id}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar
                src={item.image_url}
                variant="rounded"
                sx={{ width: 60, height: 60 }}
              />

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14
                  }}
                >
                  {item.name}
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: 13,
                    mt: 0.5
                  }}
                >
                  ₹
                  {(
                    ayujaCartPrices[item.medicine_id] ??
                    item.discounted_price ??
                    item.price
                  ).toFixed(2)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      removeFromCart(item.medicine_id)
                    }
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>

                  <Typography sx={{ fontWeight: 700 }}>
                    {item.quantity}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => addToCart(item)}
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <span>MRP Total</span>
          <strong>₹{mrpTotal.toFixed(2)}</strong>
        </Typography>

        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#16a34a',
            fontWeight: 600
          }}
        >
          <span>You Save</span>
          <span>₹{totalSavings.toFixed(2)}</span>
        </Typography>

        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 20,
            fontWeight: 700,
            color: '#006d77',
            mt: 1
          }}
        >
          <span>Total</span>
          <span>₹{sellingTotal.toFixed(2)}</span>
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="contained"
        endIcon={<ArrowForward />}
        onClick={() => {
          setShowCart(false);

          if (!patientDetails) {
            setShowPatientForm(true);
          } else {
            proceedToCheckout();
          }
        }}
        sx={{
          bgcolor: '#006d77',
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
          '&:hover': {
            bgcolor: '#005560'
          }
        }}
      >
        Proceed
      </Button>
    </>
  )}
</Drawer>

      {/* Prescription Upload Dialog */}
      <Dialog open={showPrescriptionDialog} onClose={handlePrescriptionDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>Upload Prescription</Typography>
          <IconButton onClick={handlePrescriptionDialogClose} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Prescription Required</Typography>
            <Typography variant="body2">Your cart contains medicines that require a valid prescription.</Typography>
          </Alert>
          {!prescriptionPreview ? (
            <Box
              sx={{ border: '2px dashed #cbd5e1', borderRadius: 2, p: 4, textAlign: 'center', bgcolor: '#f8fafc', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { borderColor: '#006d77', bgcolor: '#f0fdfa' } }}
              component="label"
            >
              <input type="file" hidden accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={handlePrescriptionUpload} />
              <CloudUpload sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1e293b', mb: 1 }}>Click to upload prescription</Typography>
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>Supported formats: JPG, PNG, PDF (Max 5MB)</Typography>
            </Box>
          ) : (
            <Box sx={{ border: '2px solid #d1fae5', borderRadius: 2, p: 2, bgcolor: '#f0fdf4', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: '#16a34a', mr: 1 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>Prescription uploaded successfully</Typography>
              </Box>
              {prescriptionPreview.startsWith('data:application/pdf') ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', borderRadius: 1, p: 3, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: 14, color: '#64748b' }}>PDF Document Uploaded</Typography>
                </Box>
              ) : (
                <Box component="img" src={prescriptionPreview} alt="Prescription preview" sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 1, bgcolor: '#fff', border: '1px solid #e2e8f0' }} />
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button component="label" variant="outlined" size="small" fullWidth sx={{ textTransform: 'none', borderColor: '#006d77', color: '#006d77', '&:hover': { borderColor: '#005560', bgcolor: '#f0fdfa' } }}>
                  Replace
                  <input type="file" hidden accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={handlePrescriptionUpload} />
                </Button>
                <Button variant="outlined" size="small" fullWidth onClick={handleRemovePrescription} sx={{ textTransform: 'none', borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' } }}>
                  Remove
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button onClick={handlePrescriptionDialogClose} sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handlePrescriptionDialogSubmit} variant="contained" disabled={!prescriptionFile}
            sx={{ textTransform: 'none', bgcolor: '#006d77', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#005560' }, '&:disabled': { bgcolor: '#cbd5e1', color: '#fff' } }}
          >
            Continue to Checkout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicineListScreen;